import { API, graphqlOperation, Storage } from "aws-amplify";

import { photoByParentByTime } from "../graphql/queries";
import { createPhoto, deletePhoto } from "../graphql/mutations";
import { onCreatePhoto, onDeletePhoto } from "../graphql/subscriptions";

import {
  PhotoStatus,
  PhotoSubStatus,
  MAX_MUTATION_ATTEMPTS,
  MAX_QUEUE_RETRY_ATTEMPTS,
  MIN_QUEUE_RETRY_DELAY,
  MAX_QUEUE_RETRY_DELAY,
} from "../utils/constants";
import { logError } from "../utils/appUtils";
import {
  savePendingUpload,
  getPendingUploads,
  deletePendingUpload,
  savePendingMutation,
  getPendingMutations,
  deletePendingMutation,
  clearOldUploads,
  addTombstone,
  getTombstones,
  removeTombstone,
  clearOldTombstones,
  savePendingDelete,
  getPendingDeletes,
  deletePendingDelete,
} from "../utils/uploadDb";

const queueState = {
  photos: new Map(),
  pendingUploads: new Map(),
  mutationQueue: new Map(),
  pendingDeletes: new Map(),
};
const parentMetadata = new Map();

const parentKeyIndex = new Map(); // parentId -> Set(keys)
const trackedParents = new Set();
const loadedParents = new Set();
const restoredParents = new Set();
const loadingParents = new Map();
const deletedKeys = new Set();

const listeners = new Set();

const uploadTasks = new Map();
let mutationRetryTimeout = null;
let deleteRetryTimeout = null;
let subscriptionsInitialized = false;
const activeSubscriptions = new Set();

const mutationQueueRetryCount = { current: 0 };
const mutationQueueLastDelay = { current: MIN_QUEUE_RETRY_DELAY };
const deleteQueueRetryCount = { current: 0 };
const deleteQueueLastDelay = { current: MIN_QUEUE_RETRY_DELAY };
const parentUsageCounts = new Map();
const parentCleanupTimers = new Map();
const PARENT_CLEANUP_RECHECK_DELAY = 1000;

const shouldLogRetryAttempt = (attempt, maxAttempts) =>
  attempt === 1 || attempt >= maxAttempts;

const normalizeQueueError = (errorInput, contextInput) => {
  const context =
    contextInput ||
    (errorInput &&
    typeof errorInput === "object" &&
    !(errorInput instanceof Error) &&
    !Array.isArray(errorInput) &&
    !errorInput.message
      ? errorInput
      : undefined);

  if (errorInput instanceof Error) {
    if (context) {
      errorInput.context = { ...(errorInput.context || {}), ...context };
    }
    return errorInput;
  }

  if (
    errorInput &&
    typeof errorInput === "object" &&
    errorInput.message &&
    typeof errorInput.message === "string"
  ) {
    const normalized = new Error(errorInput.message);
    normalized.originalError = errorInput;
    if (context) {
      normalized.context = context;
    }
    return normalized;
  }

  const normalized = new Error(
    typeof errorInput === "string" && errorInput.length > 0
      ? errorInput
      : "Photo transfer queue error",
  );

  if (context) {
    normalized.context = context;
  }

  if (
    errorInput &&
    typeof errorInput === "object" &&
    Object.keys(errorInput).length > 0
  ) {
    normalized.originalError = errorInput;
  }

  return normalized;
};

const logQueueError = (msg, errorInput, contextInput, notify, redirect) =>
  logError(
    msg,
    normalizeQueueError(errorInput, contextInput),
    notify,
    redirect,
  );

const getFriendlyStatusMessage = (err) => {
  const rawMessage = err?.message?.toLowerCase() || "";

  if (rawMessage.includes("validation")) {
    return "Invalid photo format or data";
  }

  if (rawMessage.includes("size")) {
    return "Photo file too large";
  }

  if (
    rawMessage.includes("permission") ||
    rawMessage.includes("unauthorized")
  ) {
    return "You do not have permission to upload this photo";
  }

  return "Upload failed. Please try again.";
};

const updateMap = (mapName, mutator) => {
  const current = queueState[mapName] || new Map();
  const next = new Map(current);
  mutator(next);
  queueState[mapName] = next;
  return next;
};

const updateState = (mapName, key, value) =>
  updateMap(mapName, (map) => {
    if (value === undefined || value === null) {
      map.delete(key);
    } else {
      map.set(key, value);
    }
  });

// Limit progress notifications to avoid thundering-herd re-renders.
const PROGRESS_UPDATE_MIN_INTERVAL = 80;
const PROGRESS_UPDATE_MIN_DELTA = 0.01;
const progressUpdateTrackers = new Map();
const PROGRESS_TRACKER_MAX_AGE = 5 * 60 * 1000;
let progressTrackerCleanupInterval = null;
const getTimeStamp = () =>
  typeof performance !== "undefined" && typeof performance.now === "function"
    ? performance.now()
    : Date.now();

const clearProgressTracking = (key) => {
  const tracker = progressUpdateTrackers.get(key);
  if (!tracker) return;
  if (tracker.timeoutId) {
    clearTimeout(tracker.timeoutId);
  }
  progressUpdateTrackers.delete(key);
};

const batchUpsertPhotos = (entries, { notifyListeners = true } = {}) => {
  if (!entries || entries.length === 0) return;
  entries.forEach(({ key, photo }) => {
    if (!key || !photo) return;
    const prev = queueState.photos.get(key) || {};
    const next = { ...prev, ...photo, key };
    updateState("photos", key, next);

    if (next.parentId) {
      if (!parentKeyIndex.has(next.parentId)) {
        parentKeyIndex.set(next.parentId, new Set());
      }
      parentKeyIndex.get(next.parentId).add(key);
    }
  });

  if (notifyListeners) {
    notify();
  }
};

const getTaskPromise = (task) => {
  if (task?.result?.then) return task.result;
  if (typeof task?.then === "function") return task;
  return Promise.resolve();
};

// Cleanup helper for ERROR photos with missing ids (query by parent/key and remove DB entries)
const deletePhotoByKey = async (key, parentId) => {
  if (!key || !parentId) return;
  let nextToken = null;

  try {
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const response = await API.graphql(
        graphqlOperation(photoByParentByTime, {
          parentId,
          limit: 100,
          nextToken,
        }),
      );

      const result = response.data?.photoByParentByTime;
      const items = result?.items || [];
      nextToken = result?.nextToken;

      if (items.length === 0 && !nextToken) {
        return;
      }

      const matches = items.filter((photo) => photo.key === key);
      if (matches.length > 0) {
        for (const photo of matches) {
          try {
            // eslint-disable-next-line no-await-in-loop
            await API.graphql(
              graphqlOperation(deletePhoto, {
                input: { id: photo.id },
              }),
            );
          } catch (deleteErr) {
            logQueueError(
              "photoTransferQueue:deletePhotoByKey:delete",
              deleteErr,
              {
                key,
                parentId,
                photoId: photo.id,
              },
            );
          }
        }
        return;
      }

      if (!nextToken) {
        return;
      }
    }
  } catch (err) {
    logQueueError("photoTransferQueue:deletePhotoByKey:query", err, {
      key,
      parentId,
    });
  }
};

const persistParentList = () => {
  try {
    localStorage.setItem(
      "photo-transfer-parents",
      JSON.stringify(Array.from(trackedParents)),
    );
  } catch (err) {
    logError("photoTransferQueue:persistParentList", err);
  }
};

const loadTrackedParentsFromStorage = () => {
  try {
    const stored = localStorage.getItem("photo-transfer-parents");
    if (stored) {
      JSON.parse(stored).forEach((parentId) => {
        if (parentId) trackedParents.add(parentId);
      });
    }
  } catch (err) {
    logError("photoTransferQueue:loadTrackedParentsFromStorage", err);
  }
};

const ensureParentTracked = (parentId) => {
  if (!parentId || trackedParents.has(parentId)) return;
  trackedParents.add(parentId);
  persistParentList();
};

const untrackParent = (parentId) => {
  if (!parentId || !trackedParents.delete(parentId)) return;
  persistParentList();
};

const removeParentIfIdle = (parentId) => {
  if (!parentId) return;
  const hasPhotos =
    (parentKeyIndex.get(parentId) && parentKeyIndex.get(parentId).size > 0) ||
    Array.from(queueState.pendingUploads.values()).some(
      ({ mutationInput }) => mutationInput?.parentId === parentId,
    ) ||
    Array.from(queueState.mutationQueue.values()).some(
      ({ input }) => input?.parentId === parentId,
    ) ||
    Array.from(queueState.pendingDeletes.values()).some(
      (pending) => pending.parentId === parentId,
    );

  if (hasPhotos) return;
  untrackParent(parentId);
  restoredParents.delete(parentId);
  loadedParents.delete(parentId);
};

const hasActiveParentWork = (parentId) => {
  if (!parentId) return false;
  const hasUploadingPhoto = Array.from(parentKeyIndex.get(parentId) || []).some(
    (key) => {
      const photo = queueState.photos.get(key);
      return photo?.status === PhotoStatus.UPLOADING;
    },
  );
  if (hasUploadingPhoto) return true;

  const uploadsInQueue = Array.from(queueState.pendingUploads.values()).some(
    ({ mutationInput }) => mutationInput?.parentId === parentId,
  );
  if (uploadsInQueue) return true;

  const activeUploadTasks = Array.from(uploadTasks.keys()).some((key) => {
    const photo = queueState.photos.get(key);
    return photo?.parentId === parentId;
  });
  if (activeUploadTasks) return true;

  const mutationsPending = Array.from(queueState.mutationQueue.values()).some(
    ({ input }) => input?.parentId === parentId,
  );
  if (mutationsPending) return true;

  const deletesPending = Array.from(queueState.pendingDeletes.values()).some(
    (pending) => pending.parentId === parentId,
  );
  return deletesPending;
};

const cleanupParentResources = (parentId) => {
  if (!parentId) return;

  if (hasActiveParentWork(parentId)) {
    if (!parentCleanupTimers.has(parentId)) {
      const timeoutId = setTimeout(() => {
        parentCleanupTimers.delete(parentId);
        cleanupParentResources(parentId);
      }, PARENT_CLEANUP_RECHECK_DELAY);
      parentCleanupTimers.set(parentId, timeoutId);
    }
    return;
  }

  if (parentCleanupTimers.has(parentId)) {
    clearTimeout(parentCleanupTimers.get(parentId));
    parentCleanupTimers.delete(parentId);
  }

  untrackParent(parentId);

  const keys = parentKeyIndex.get(parentId);
  let removed = false;
  if (keys && keys.size > 0) {
    const keyList = Array.from(keys);
    keyList.forEach((key) => {
      clearProgressTracking(key);
    });
    updateMap("photos", (photosMap) => {
      keyList.forEach((key) => {
        photosMap.delete(key);
      });
    });
    parentKeyIndex.delete(parentId);
    removed = true;
  }

  if (loadedParents.has(parentId)) {
    loadedParents.delete(parentId);
    removed = true;
  }

  if (restoredParents.has(parentId)) {
    restoredParents.delete(parentId);
    removed = true;
  }

  if (parentMetadata.has(parentId)) {
    parentMetadata.delete(parentId);
    removed = true;
  }

  if (removed) {
    notify();
  }
};

const setParentMetadata = (parentId, metadata = {}) => {
  if (!parentId || !metadata) return;
  const hasEntries = Object.keys(metadata).length > 0;
  if (!hasEntries) return;

  const existing = parentMetadata.get(parentId) || {};
  const next = { ...existing, ...metadata };
  const changed = Object.keys(next).some((key) => existing[key] !== next[key]);

  if (!changed) return;

  parentMetadata.set(parentId, next);
  notify();
};

const retainParent = (parentId) => {
  if (!parentId) return;
  const nextCount = (parentUsageCounts.get(parentId) || 0) + 1;
  parentUsageCounts.set(parentId, nextCount);

  if (parentCleanupTimers.has(parentId)) {
    clearTimeout(parentCleanupTimers.get(parentId));
    parentCleanupTimers.delete(parentId);
  }
};

const releaseParent = (parentId) => {
  if (!parentId) return;
  const count = parentUsageCounts.get(parentId) || 0;
  if (count > 1) {
    parentUsageCounts.set(parentId, count - 1);
    return;
  }

  if (count === 1) {
    parentUsageCounts.delete(parentId);
    cleanupParentResources(parentId);
  }
};

const getSnapshot = () => ({
  photos: queueState.photos,
  pendingUploads: queueState.pendingUploads,
  mutationQueue: queueState.mutationQueue,
  pendingDeletes: queueState.pendingDeletes,
  pendingUploadsSize: queueState.pendingUploads.size,
  queueSize: queueState.mutationQueue.size,
  pendingDeletesSize: queueState.pendingDeletes.size,
  parentMetadata,
});

const notify = () => {
  const snapshot = getSnapshot();
  listeners.forEach((listener) => {
    try {
      listener(snapshot);
    } catch (err) {
      logError("photoTransferQueue:listener", err);
    }
  });
};

const upsertPhoto = (key, photo) => {
  if (!key || !photo) return;
  batchUpsertPhotos([{ key, photo }]);
};

const shouldAllowProgressUpdate = (key) => {
  const photo = queueState.photos.get(key);
  return !photo || photo.status === PhotoStatus.UPLOADING;
};

const emitProgressUpdate = (key, patch, tracker, timestamp) => {
  if (!shouldAllowProgressUpdate(key)) {
    clearProgressTracking(key);
    return;
  }

  if (tracker) {
    tracker.lastEmit = timestamp;
    if (typeof patch.progress === "number") {
      tracker.lastProgress = patch.progress;
    }
    tracker.pending = null;
    if (tracker.timeoutId) {
      clearTimeout(tracker.timeoutId);
      tracker.timeoutId = null;
    }
    progressUpdateTrackers.set(key, tracker);
  }

  upsertPhoto(key, patch);
};

const queueProgressUpdate = (key, patch) => {
  if (!key || !patch) return;

  const timestamp = getTimeStamp();
  const tracker = progressUpdateTrackers.get(key) || {
    lastEmit: 0,
    lastProgress: 0,
    timeoutId: null,
    pending: null,
  };

  const nextProgress =
    typeof patch.progress === "number" ? patch.progress : undefined;
  const progressDelta =
    nextProgress === undefined || tracker.lastProgress === undefined
      ? Infinity
      : Math.abs(nextProgress - tracker.lastProgress);
  const timeElapsed = timestamp - tracker.lastEmit;
  const shouldForceEmit = nextProgress !== undefined && nextProgress >= 1;

  if (
    shouldForceEmit ||
    timeElapsed >= PROGRESS_UPDATE_MIN_INTERVAL ||
    (nextProgress !== undefined && progressDelta >= PROGRESS_UPDATE_MIN_DELTA)
  ) {
    emitProgressUpdate(key, patch, tracker, timestamp);
    if (shouldForceEmit) {
      clearProgressTracking(key);
    }
    return;
  }

  tracker.pending = patch;
  if (!tracker.timeoutId) {
    tracker.timeoutId = setTimeout(() => {
      const activeTracker = progressUpdateTrackers.get(key);
      if (!activeTracker) {
        return;
      }

      const pendingPatch = activeTracker.pending;
      activeTracker.pending = null;
      activeTracker.timeoutId = null;

      if (!pendingPatch) {
        progressUpdateTrackers.set(key, activeTracker);
        return;
      }

      emitProgressUpdate(key, pendingPatch, activeTracker, getTimeStamp());
    }, PROGRESS_UPDATE_MIN_INTERVAL);
  }

  progressUpdateTrackers.set(key, tracker);
};

const sweepStaleProgressTrackers = () => {
  const now = getTimeStamp();
  progressUpdateTrackers.forEach((tracker, key) => {
    if (!tracker) return;
    const isStale = now - tracker.lastEmit >= PROGRESS_TRACKER_MAX_AGE;
    const photo = queueState.photos.get(key);
    const isActiveUpload = photo?.status === PhotoStatus.UPLOADING;
    if (isStale && !isActiveUpload) {
      clearProgressTracking(key);
    }
  });
};

const removePhoto = (key) => {
  if (!queueState.photos.has(key)) return;
  clearProgressTracking(key);
  const photo = queueState.photos.get(key);
  updateState("photos", key, null);

  if (photo?.parentId && parentKeyIndex.has(photo.parentId)) {
    const set = parentKeyIndex.get(photo.parentId);
    set.delete(key);
    if (set.size === 0) {
      parentKeyIndex.delete(photo.parentId);
      removeParentIfIdle(photo.parentId);
    }
  }
  notify();
};

const subscribe = (listener) => {
  listeners.add(listener);
  listener(getSnapshot());
  return () => listeners.delete(listener);
};

const fetchParentPhotos = async (parentId) => {
  if (!parentId || loadedParents.has(parentId)) return;
  let nextToken = null;
  const entries = [];

  do {
    // eslint-disable-next-line no-await-in-loop
    const res = await API.graphql(
      graphqlOperation(photoByParentByTime, { parentId, nextToken }),
    );
    const result = res.data.photoByParentByTime;
    const items = result.items || [];
    items.forEach((item) => {
      if (deletedKeys.has(item.key)) return;
      entries.push({
        key: item.key,
        photo: {
          ...item,
          status: PhotoStatus.DONE,
          progress: 1,
          statusMessage: null,
          subStatus: null,
        },
      });
    });
    nextToken = result.nextToken;
  } while (nextToken);

  batchUpsertPhotos(entries, { notifyListeners: false });
  loadedParents.add(parentId);
  notify();
};

const handleCreateSubscription = ({ value }) => {
  try {
    const p = { ...value.data.onCreatePhoto, status: PhotoStatus.DONE };
    if (deletedKeys.has(p.key)) return;
    upsertPhoto(p.key, {
      ...p,
      progress: 1,
      subStatus: null,
      statusMessage: null,
    });
  } catch (err) {
    logError("photoTransferQueue:onCreatePhoto", err);
  }
};

const handleDeleteSubscription = ({ value }) => {
  try {
    const p = value.data.onDeletePhoto;
    removePhoto(p.key);
    uploadTasks.delete(p.key);
    updateState("mutationQueue", p.key, null);
    updateState("pendingUploads", p.key, null);
    updateState("pendingDeletes", p.key, null);
    notify();

    deletePendingUpload(p.key).catch((err) =>
      logError("photoTransferQueue:deletePendingUpload:subscription", err),
    );
    deletePendingMutation(p.key).catch((err) =>
      logError("photoTransferQueue:deletePendingMutation:subscription", err),
    );
  } catch (err) {
    logError("photoTransferQueue:onDeletePhoto", err);
  }
};

const cleanupSubscriptions = () => {
  if (activeSubscriptions.size === 0) {
    subscriptionsInitialized = false;
    return;
  }
  activeSubscriptions.forEach((subscription) => {
    try {
      if (typeof subscription?.unsubscribe === "function") {
        subscription.unsubscribe();
      }
    } catch (unsubscribeErr) {
      logError("photoTransferQueue:cleanupSubscriptions", unsubscribeErr);
    }
  });
  activeSubscriptions.clear();
  subscriptionsInitialized = false;
};

const initSubscriptions = () => {
  if (subscriptionsInitialized) return;
  try {
    const createSubscription = API.graphql(
      graphqlOperation(onCreatePhoto),
    ).subscribe({
      next: handleCreateSubscription,
    });
    const deleteSubscription = API.graphql(
      graphqlOperation(onDeletePhoto),
    ).subscribe({
      next: handleDeleteSubscription,
    });
    if (createSubscription?.unsubscribe) {
      activeSubscriptions.add(createSubscription);
    }
    if (deleteSubscription?.unsubscribe) {
      activeSubscriptions.add(deleteSubscription);
    }
    subscriptionsInitialized = true;
  } catch (err) {
    logError("photoTransferQueue:initSubscriptions", err);
    cleanupSubscriptions();
  }
};

const loadQueueFromDB = async (
  fetchItems,
  mapName,
  {
    buildStateValue,
    buildPhotoEntry,
    handleDeleted,
    ignoreTombstones = false,
  } = {},
) => {
  const items = await fetchItems();
  const photoEntries = [];
  const stateUpdates = [];

  items.forEach((item) => {
    const key = item?.key;
    if (!key) return;

    if (!ignoreTombstones && deletedKeys.has(key)) {
      if (handleDeleted) {
        handleDeleted(item);
      }
      return;
    }

    if (mapName && buildStateValue) {
      const value = buildStateValue(item);
      if (value !== undefined) {
        stateUpdates.push({ key, value });
      }
    }

    if (buildPhotoEntry) {
      const photo = buildPhotoEntry(item);
      if (photo) {
        photoEntries.push({ key, photo });
      }
    }
  });

  if (mapName && stateUpdates.length > 0) {
    updateMap(mapName, (map) => {
      stateUpdates.forEach(({ key, value }) => {
        map.set(key, value);
      });
    });
  }

  return { items, photoEntries };
};

const restoreParentState = async (parentId) => {
  if (!parentId || restoredParents.has(parentId)) return;

  try {
    const tombstones = await getTombstones(parentId);
    tombstones.forEach((key) => deletedKeys.add(key));

    await clearOldUploads();
    await clearOldTombstones();

    const [uploadsResult, mutationsResult, deletesResult] = await Promise.all([
      loadQueueFromDB(() => getPendingUploads(parentId), "pendingUploads", {
        buildStateValue: ({
          file,
          mutationInput,
          parentLabel: storedParentLabel,
        }) => ({ file, mutationInput, parentLabel: storedParentLabel }),
        buildPhotoEntry: ({
          key,
          file,
          mutationInput,
          parentLabel: storedParentLabel,
        }) => ({
          key,
          parentId,
          parentLabel: storedParentLabel,
          name: mutationInput?.name || file?.name,
          status: PhotoStatus.UPLOADING,
          file,
          progress: 0,
          isPaused: !navigator.onLine,
          statusMessage: navigator.onLine
            ? "Resuming upload..."
            : "Waiting for connection...",
        }),
        handleDeleted: ({ key }) =>
          deletePendingUpload(key).catch((err) =>
            logError("photoTransferQueue:deletePendingUpload:restore", err),
          ),
      }),
      loadQueueFromDB(() => getPendingMutations(parentId), "mutationQueue", {
        buildStateValue: ({ mutationInput, attempts }) => ({
          input: mutationInput,
          attempts: attempts || 0,
        }),
        buildPhotoEntry: ({ key, mutationInput }) => ({
          key,
          parentId,
          name: mutationInput?.name,
          status: PhotoStatus.UPLOADING,
          subStatus: PhotoSubStatus.UPLOADED,
          progress: 1,
          statusMessage: "Waiting to save...",
        }),
        handleDeleted: ({ key }) =>
          deletePendingMutation(key).catch((err) =>
            logError("photoTransferQueue:deletePendingMutation:restore", err),
          ),
      }),
      loadQueueFromDB(() => getPendingDeletes(parentId), "pendingDeletes", {
        buildStateValue: ({ key, photoId, attempts }) => ({
          key,
          photoId,
          parentId,
          attempts: attempts || 0,
        }),
        ignoreTombstones: true,
      }),
    ]);

    const photoEntries = [
      ...uploadsResult.photoEntries,
      ...mutationsResult.photoEntries,
    ];

    if (navigator.onLine) {
      if (uploadsResult.items.length > 0) {
        setTimeout(() => startPendingUploads(), 1000);
      }
      if (mutationsResult.items.length > 0) {
        setTimeout(() => processMutationQueue(), 1000);
      }
      if (deletesResult.items.length > 0) {
        setTimeout(() => processDeleteQueue(), 1000);
      }
    }

    if (photoEntries.length > 0) {
      batchUpsertPhotos(photoEntries, { notifyListeners: false });
    }
    notify();
    restoredParents.add(parentId);
  } catch (err) {
    logError("photoTransferQueue:restoreParentState", err);
    throw err;
  }
};

const ensureParentLoaded = async (parentId) => {
  if (!parentId) return;
  ensureParentTracked(parentId);
  if (loadingParents.has(parentId)) {
    return loadingParents.get(parentId);
  }

  const loadPromise = (async () => {
    try {
      await fetchParentPhotos(parentId);
      await restoreParentState(parentId);
    } catch (err) {
      logQueueError("photoTransferQueue:ensureParentLoaded", err, {
        parentId,
      });
      throw err;
    } finally {
      loadingParents.delete(parentId);
    }
  })();

  loadingParents.set(parentId, loadPromise);
  return loadPromise;
};

const init = () => {
  loadTrackedParentsFromStorage();
  initSubscriptions();
  if (typeof window !== "undefined") {
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
  }
  if (!progressTrackerCleanupInterval) {
    progressTrackerCleanupInterval = setInterval(
      sweepStaleProgressTrackers,
      60 * 1000,
    );
  }
  trackedParents.forEach((parentId) => {
    retainParent(parentId);
    ensureParentLoaded(parentId)
      .catch(() => {
        // Logged in ensureParentLoaded; swallow so this fire-and-forget preload does not reject unhandled.
      })
      .finally(() => {
        releaseParent(parentId);
      });
  });
};

const destroy = () => {
  cleanupSubscriptions();
  if (typeof window !== "undefined") {
    window.removeEventListener("online", handleOnline);
    window.removeEventListener("offline", handleOffline);
  }
  if (progressTrackerCleanupInterval) {
    clearInterval(progressTrackerCleanupInterval);
    progressTrackerCleanupInterval = null;
  }
  listeners.clear();
};

const getParentPhotos = (parentId) => {
  if (!parentId) return [];
  const keys = parentKeyIndex.get(parentId);
  if (!keys) return [];
  return Array.from(keys)
    .map((key) => queueState.photos.get(key))
    .filter(Boolean);
};

const handleOnline = () => {
  if (queueState.pendingDeletes.size > 0) {
    processDeleteQueue();
  }

  uploadTasks.forEach((task, key) => {
    if (task?.resume) {
      task.resume();
    }
    upsertPhoto(key, { isPaused: false, statusMessage: null });
  });

  if (queueState.pendingUploads.size > 0) {
    startPendingUploads();
  }

  if (queueState.mutationQueue.size > 0) {
    processMutationQueue();
  }
};

const handleOffline = () => {
  uploadTasks.forEach((task, key) => {
    if (task?.pause) {
      task.pause();
    }
    const photo = queueState.photos.get(key);
    if (photo && photo.status === PhotoStatus.UPLOADING) {
      clearProgressTracking(key);
      upsertPhoto(key, {
        isPaused: true,
        statusMessage: "Paused (offline)",
      });
    }
  });

  if (mutationRetryTimeout) {
    clearTimeout(mutationRetryTimeout);
    mutationRetryTimeout = null;
  }

  if (deleteRetryTimeout) {
    clearTimeout(deleteRetryTimeout);
    deleteRetryTimeout = null;
  }
};

const getJitter = (baseDelay) => baseDelay + Math.random() * baseDelay * 0.3;

const retryWithBackoff = async (fn, isNetworkError = false) => {
  let attempt = 0;
  let delay = 500;
  const maxDelay = 30000;
  const maxNetworkAttempts = 10;
  const maxNonNetworkAttempts = 3;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    attempt += 1;
    try {
      // eslint-disable-next-line no-await-in-loop
      return await fn();
    } catch (err) {
      const networkIssue =
        isNetworkError ||
        err.message?.includes("Network") ||
        err.message?.includes("Failed to fetch") ||
        !navigator.onLine;
      if (networkIssue && attempt >= maxNetworkAttempts) throw err;
      if (!networkIssue && attempt >= maxNonNetworkAttempts) throw err;
      const waitTime = Math.min(getJitter(delay), maxDelay);
      // eslint-disable-next-line no-await-in-loop
      await new Promise((resolve) => setTimeout(resolve, waitTime));
      delay = Math.min(delay * 2, maxDelay);
    }
  }
};

const saveQueueToStorage = () => {
  try {
    queueState.mutationQueue.forEach((mutationData, key) => {
      const parentId = mutationData.input?.parentId;
      if (!parentId) return;
      savePendingMutation(
        key,
        mutationData.input,
        parentId,
        mutationData.attempts || 0,
      ).catch((err) => {
        logError("photoTransferQueue:savePendingMutation:storage", err);
      });
    });
  } catch (err) {
    logError("photoTransferQueue:saveQueueToStorage", err);
  }
};

const processMutationQueue = async () => {
  if (!navigator.onLine || queueState.mutationQueue.size === 0) {
    return;
  }

  const queue = Array.from(queueState.mutationQueue.entries());
  if (queue.length === 0) return;

  for (let i = 0; i < queue.length; i += 1) {
    const [key, mutationData] = queue[i];

    if (!queueState.mutationQueue.has(key)) {
      continue;
    }

    const currentPhoto = queueState.photos.get(key);
    const shouldSkip =
      !currentPhoto || currentPhoto.status === PhotoStatus.DONE;

    if (shouldSkip) {
      updateState("mutationQueue", key, null);
      notify();
      saveQueueToStorage();

      // eslint-disable-next-line no-await-in-loop
      await deletePendingMutation(key).catch((err) =>
        logError("photoTransferQueue:deletePendingMutation:skip", err),
      );
      continue;
    }

    if (currentPhoto?.subStatus === PhotoSubStatus.RETRYING) {
      continue;
    }

    const attempts = (mutationData.attempts || 0) + 1;
    if (attempts > MAX_MUTATION_ATTEMPTS) {
      updateState("mutationQueue", key, null);
      notify();
      saveQueueToStorage();

      // eslint-disable-next-line no-await-in-loop
      await deletePendingMutation(key).catch((err) =>
        logError("photoTransferQueue:deletePendingMutation:maxAttempts", err),
      );

      logQueueError(
        "photoTransferQueue:processMutationQueue:maxAttemptsExceeded",
        new Error(`Mutation failed after ${MAX_MUTATION_ATTEMPTS} attempts`),
        {
          key,
          parentId: mutationData.input?.parentId,
          attempts,
          maxAttempts: MAX_MUTATION_ATTEMPTS,
          mutationInput: mutationData.input,
        },
      );

      upsertPhoto(key, {
        status: PhotoStatus.ERROR,
        error: `Failed after ${MAX_MUTATION_ATTEMPTS} attempts`,
        statusMessage: "Unable to save photo. Please try again.",
      });
      continue;
    }

    upsertPhoto(key, {
      subStatus: PhotoSubStatus.RETRYING,
      statusMessage: "Saving...",
    });

    try {
      // eslint-disable-next-line no-await-in-loop
      const resp = await API.graphql(
        graphqlOperation(createPhoto, {
          input: mutationData.input,
        }),
      );

      updateState("mutationQueue", key, null);
      notify();
      saveQueueToStorage();

      // eslint-disable-next-line no-await-in-loop
      await deletePendingMutation(key).catch((err) =>
        logError("photoTransferQueue:deletePendingMutation:success", err),
      );

      const newPhoto = resp.data.createPhoto;
      upsertPhoto(key, {
        ...newPhoto,
        status: PhotoStatus.DONE,
        progress: 1,
        subStatus: null,
        statusMessage: null,
        isPaused: false,
      });
    } catch (err) {
      const isNetworkErr =
        err.message?.includes("Network") ||
        err.message?.includes("Failed to fetch") ||
        !navigator.onLine;

      if (
        !isNetworkErr &&
        shouldLogRetryAttempt(attempts, MAX_MUTATION_ATTEMPTS)
      ) {
        logQueueError(
          "photoTransferQueue:processMutationQueue:retry_failed",
          err,
          {
            key,
            parentId: mutationData.input?.parentId,
            attempt: attempts,
            maxAttempts: MAX_MUTATION_ATTEMPTS,
            errorMessage: err.message,
          },
        );
      }

      updateState("mutationQueue", key, {
        ...mutationData,
        attempts,
      });
      notify();
      saveQueueToStorage();

      if (navigator.onLine) {
        upsertPhoto(key, {
          subStatus: PhotoSubStatus.RETRYING,
          statusMessage: "Retrying...",
        });
      } else {
        upsertPhoto(key, {
          subStatus: PhotoSubStatus.PAUSED,
          statusMessage: "Paused (offline)",
          isPaused: true,
        });
      }
    }

    if (i < queue.length - 1) {
      // eslint-disable-next-line no-await-in-loop
      await new Promise((resolve) => setTimeout(resolve, 500));
    }
  }

  if (queueState.mutationQueue.size > 0 && navigator.onLine) {
    mutationQueueRetryCount.current += 1;
    if (mutationQueueRetryCount.current > MAX_QUEUE_RETRY_ATTEMPTS) {
      const remainingKeys = Array.from(queueState.mutationQueue.keys());
      logQueueError(
        "photoTransferQueue:processMutationQueue:maxQueueRetriesExceeded",
        new Error("Mutation queue retry limit exceeded"),
        {
          remainingItems: remainingKeys.length,
          items: remainingKeys,
        },
      );

      const failedPhotoEntries = remainingKeys.map((key) => ({
        key,
        photo: {
          status: PhotoStatus.ERROR,
          subStatus: null,
          error: "Failed after queue retry limit",
          statusMessage: "Unable to save photo. Please try again.",
        },
      }));

      updateMap("mutationQueue", (map) => {
        map.clear();
      });
      batchUpsertPhotos(failedPhotoEntries, { notifyListeners: false });
      notify();

      await Promise.all(
        remainingKeys.map((key) =>
          deletePendingMutation(key).catch((err) =>
            logError(
              "photoTransferQueue:deletePendingMutation:queueGiveUp",
              err,
            ),
          ),
        ),
      );

      mutationQueueRetryCount.current = 0;
      mutationQueueLastDelay.current = MIN_QUEUE_RETRY_DELAY;

      if (mutationRetryTimeout) {
        clearTimeout(mutationRetryTimeout);
        mutationRetryTimeout = null;
      }
      return;
    }

    const delay = Math.min(
      mutationQueueLastDelay.current * 2,
      MAX_QUEUE_RETRY_DELAY,
    );
    mutationQueueLastDelay.current = delay;

    mutationRetryTimeout = setTimeout(() => {
      processMutationQueue();
    }, delay);
  } else {
    mutationQueueRetryCount.current = 0;
    mutationQueueLastDelay.current = MIN_QUEUE_RETRY_DELAY;

    if (mutationRetryTimeout) {
      clearTimeout(mutationRetryTimeout);
      mutationRetryTimeout = null;
    }
  }
};

const processDeleteQueue = async () => {
  if (!navigator.onLine || queueState.pendingDeletes.size === 0) {
    return;
  }

  const queue = Array.from(queueState.pendingDeletes.entries());
  if (queue.length === 0) return;

  for (let i = 0; i < queue.length; i += 1) {
    const [key, deleteData] = queue[i];

    const attempts = (deleteData.attempts || 0) + 1;

    if (attempts > MAX_MUTATION_ATTEMPTS) {
      logQueueError(
        "photoTransferQueue:processDeleteQueue:maxAttempts",
        new Error("Delete queue attempt limit exceeded"),
        {
          key,
          attempts: MAX_MUTATION_ATTEMPTS,
        },
      );

      updateState("pendingDeletes", key, null);
      notify();
      // eslint-disable-next-line no-await-in-loop
      await deletePendingDelete(key).catch((err) =>
        logError("photoTransferQueue:deletePendingDelete:maxAttempts", err),
      );

      continue;
    }

    try {
      // eslint-disable-next-line no-await-in-loop
      await Storage.remove(key);

      if (deleteData.photoId) {
        try {
          // eslint-disable-next-line no-await-in-loop
          await API.graphql(
            graphqlOperation(deletePhoto, {
              input: { id: deleteData.photoId },
            }),
          );
        } catch (err) {
          logQueueError(
            "photoTransferQueue:processDeleteQueue:deleteById",
            err,
            {
              key,
              parentId: deleteData.parentId,
              photoId: deleteData.photoId,
            },
          );
          // eslint-disable-next-line no-await-in-loop
          await deletePhotoByKey(key, deleteData.parentId);
        }
      } else {
        // eslint-disable-next-line no-await-in-loop
        await deletePhotoByKey(key, deleteData.parentId);
      }

      updateState("pendingDeletes", key, null);
      notify();
      // eslint-disable-next-line no-await-in-loop
      await deletePendingDelete(key);
      try {
        // eslint-disable-next-line no-await-in-loop
        await removeTombstone(key);
        deletedKeys.delete(key);
      } catch (err) {
        logQueueError(
          "photoTransferQueue:processDeleteQueue:removeTombstone",
          err,
          { key },
        );
      }
    } catch (err) {
      if (shouldLogRetryAttempt(attempts, MAX_MUTATION_ATTEMPTS)) {
        logQueueError(
          "photoTransferQueue:processDeleteQueue:deleteFailed",
          err,
          {
            key,
            attempt: attempts,
            errorMessage: err.message,
          },
        );
      }

      const updatedData = { ...deleteData, attempts };
      updateState("pendingDeletes", key, updatedData);
      notify();

      // eslint-disable-next-line no-await-in-loop
      await savePendingDelete(
        key,
        deleteData.photoId,
        deleteData.parentId,
        attempts,
      ).catch((e) => logError("photoTransferQueue:savePendingDelete:retry", e));
    }

    if (i < queue.length - 1) {
      // eslint-disable-next-line no-await-in-loop
      await new Promise((resolve) => setTimeout(resolve, 500));
    }
  }

  if (queueState.pendingDeletes.size > 0 && navigator.onLine) {
    deleteQueueRetryCount.current += 1;

    if (deleteQueueRetryCount.current > MAX_QUEUE_RETRY_ATTEMPTS) {
      const remainingKeys = Array.from(queueState.pendingDeletes.keys());
      logQueueError(
        "photoTransferQueue:processDeleteQueue:maxQueueRetriesExceeded",
        new Error("Delete queue retry limit exceeded"),
        {
          remainingItems: remainingKeys.length,
          items: remainingKeys,
        },
      );

      updateMap("pendingDeletes", (map) => {
        map.clear();
      });
      notify();

      remainingKeys.forEach((key) => {
        deletePendingDelete(key).catch((err) =>
          logError("photoTransferQueue:deletePendingDelete:giveUp", err),
        );
      });

      deleteQueueRetryCount.current = 0;
      deleteQueueLastDelay.current = MIN_QUEUE_RETRY_DELAY;

      if (deleteRetryTimeout) {
        clearTimeout(deleteRetryTimeout);
        deleteRetryTimeout = null;
      }
      return;
    }

    const delay = Math.min(
      deleteQueueLastDelay.current * 2,
      MAX_QUEUE_RETRY_DELAY,
    );
    deleteQueueLastDelay.current = delay;

    deleteRetryTimeout = setTimeout(() => {
      processDeleteQueue();
    }, delay);
  } else {
    deleteQueueRetryCount.current = 0;
    deleteQueueLastDelay.current = MIN_QUEUE_RETRY_DELAY;

    if (deleteRetryTimeout) {
      clearTimeout(deleteRetryTimeout);
      deleteRetryTimeout = null;
    }
  }
};

const startPendingUploads = () => {
  if (!navigator.onLine || queueState.pendingUploads.size === 0) {
    return;
  }

  const pending = Array.from(queueState.pendingUploads.entries());

  pending.forEach(([key, { file, mutationInput, parentLabel }]) => {
    if (deletedKeys.has(key)) {
      return;
    }

    // Skip if an upload task is already in progress for this key
    if (uploadTasks.has(key)) {
      return;
    }

    clearProgressTracking(key);
    upsertPhoto(key, {
      parentLabel,
      name: mutationInput?.name || file?.name,
      statusMessage: "Starting upload...",
      isPaused: false,
    });

    const task = Storage.put(key, file, {
      level: "public",
      resumable: true,
      progressCallback: ({ loaded, total }) => {
        if (!total) return;
        if (deletedKeys.has(key)) return;
        const progress = Math.min(loaded / total, 1);
        queueProgressUpdate(key, {
          progress,
          statusMessage:
            progress < 1
              ? `Uploading... ${Math.round(progress * 100)}%`
              : "Upload complete, saving...",
        });
      },
      completeCallback: () => {
        uploadTasks.delete(key);
        if (deletedKeys.has(key)) return;
        clearProgressTracking(key);
        upsertPhoto(key, {
          progress: 1,
          isPaused: false,
          statusMessage: "Upload complete, saving...",
        });
      },
      errorCallback: (err) => {
        uploadTasks.delete(key);
        if (deletedKeys.has(key)) return;
        clearProgressTracking(key);
        const isNetworkErr =
          err.message?.includes("Network") ||
          err.message?.includes("Failed to fetch") ||
          !navigator.onLine;

        if (isNetworkErr) {
          updateState("pendingUploads", key, {
            file,
            mutationInput,
            parentLabel,
          });
          notify();

          savePendingUpload(
            key,
            file,
            mutationInput,
            mutationInput.parentId,
            parentLabel,
          ).catch((saveErr) => {
            logError(
              "photoTransferQueue:savePendingUpload:errorCallback",
              saveErr,
            );
          });

          upsertPhoto(key, {
            status: PhotoStatus.UPLOADING,
            isPaused: true,
            statusMessage: "Paused (offline)",
          });
        } else {
          updateState("pendingUploads", key, null);
          notify();

          deletePendingUpload(key).catch((deleteErr) =>
            logError(
              "photoTransferQueue:deletePendingUpload:errorCallback:pending",
              deleteErr,
            ),
          );

          logQueueError(
            "photoTransferQueue:resumableUpload:errorCallback",
            err,
            {
              key,
              parentId: mutationInput?.parentId,
              fileName: file?.name,
              fileSize: file?.size,
              fileType: file?.type,
              errorMessage: err.message,
              errorStack: err.stack,
            },
          );

          upsertPhoto(key, {
            status: PhotoStatus.ERROR,
            error: err.message,
            statusMessage: getFriendlyStatusMessage(err),
          });
        }
      },
    });

    uploadTasks.set(key, task);

    getTaskPromise(task)
      .then(async () => {
        if (deletedKeys.has(key)) {
          throw new Error("ZOMBIE_DETECTED");
        }

        updateState("mutationQueue", key, {
          input: mutationInput,
          attempts: 0,
        });
        notify();

        await savePendingMutation(
          key,
          mutationInput,
          mutationInput.parentId,
          0,
        );

        if (deletedKeys.has(key)) {
          await deletePendingMutation(key);
          updateState("mutationQueue", key, null);
          notify();
          throw new Error("ZOMBIE_DETECTED");
        }

        updateState("pendingUploads", key, null);
        notify();

        await deletePendingUpload(key).catch((err) =>
          logError("photoTransferQueue:deletePendingUpload:transition", err),
        );
      })
      .then(() => {
        if (deletedKeys.has(key)) {
          return;
        }

        clearProgressTracking(key);
        upsertPhoto(key, {
          status: PhotoStatus.UPLOADING,
          subStatus: PhotoSubStatus.UPLOADED,
          statusMessage: "Saving...",
        });

        if (navigator.onLine) {
          setTimeout(() => processMutationQueue(), 1000);
        }
      })
      .catch((err) => {
        if (err.message === "ZOMBIE_DETECTED") {
          return;
        }

        clearProgressTracking(key);
        const isNetworkErr =
          err.message?.includes("Network") ||
          err.message?.includes("Failed to fetch") ||
          !navigator.onLine;

        if (isNetworkErr) {
          if (!deletedKeys.has(key)) {
            updateState("pendingUploads", key, {
              file,
              mutationInput,
              parentLabel,
            });
            notify();

            savePendingUpload(
              key,
              file,
              mutationInput,
              mutationInput.parentId,
              parentLabel,
            ).catch((saveErr) => {
              logError("photoTransferQueue:savePendingUpload:catch", saveErr);
            });

            upsertPhoto(key, {
              status: PhotoStatus.UPLOADING,
              isPaused: true,
              statusMessage: "Paused (offline)",
            });
          }
        } else if (!deletedKeys.has(key)) {
          logQueueError(
            "photoTransferQueue:resumableUpload:promiseCatch",
            err,
            {
              key,
              parentId: mutationInput?.parentId,
              fileName: file?.name,
              fileSize: file?.size,
              fileType: file?.type,
              errorMessage: err.message,
              errorStack: err.stack,
            },
          );

          upsertPhoto(key, {
            status: PhotoStatus.ERROR,
            error: err.message,
            statusMessage: getFriendlyStatusMessage(err),
          });
        }
      });
  });
};

const startUpload = async ({
  key,
  file,
  mutationInput,
  parentId,
  parentLabel,
}) => {
  if (!key || !file || !parentId) {
    throw new Error("Missing upload information");
  }

  // Prevent duplicate uploads - if a task already exists for this key, skip
  if (uploadTasks.has(key)) {
    return true;
  }

  await savePendingUpload(key, file, mutationInput, parentId, parentLabel);

  ensureParentTracked(parentId);
  clearProgressTracking(key);
  upsertPhoto(key, {
    key,
    parentId,
    parentLabel,
    name: mutationInput?.name || file?.name,
    status: PhotoStatus.UPLOADING,
    file,
    progress: 0,
    isPaused: !navigator.onLine,
    statusMessage: navigator.onLine
      ? "Starting upload..."
      : "Saved offline, waiting for connection...",
  });

  updateState("pendingUploads", key, { file, mutationInput, parentLabel });
  notify();

  if (!navigator.onLine) {
    return true;
  }

  const task = Storage.put(key, file, {
    level: "public",
    resumable: true,
    progressCallback: ({ loaded, total }) => {
      if (!total) return;
      if (deletedKeys.has(key)) return;
      const progress = Math.min(loaded / total, 1);
      queueProgressUpdate(key, {
        progress,
        statusMessage:
          progress < 1
            ? `Uploading... ${Math.round(progress * 100)}%`
            : "Upload complete, saving...",
      });
    },
    completeCallback: () => {
      uploadTasks.delete(key);

      if (deletedKeys.has(key)) return;

      clearProgressTracking(key);
      upsertPhoto(key, {
        progress: 1,
        isPaused: false,
        statusMessage: "Upload complete, saving...",
      });
    },
    errorCallback: (err) => {
      uploadTasks.delete(key);

      if (deletedKeys.has(key)) return;

      clearProgressTracking(key);
      const isNetworkErr =
        err.message?.includes("Network") ||
        err.message?.includes("Failed to fetch") ||
        !navigator.onLine;

      if (isNetworkErr) {
        upsertPhoto(key, {
          status: PhotoStatus.UPLOADING,
          isPaused: true,
          statusMessage: "Paused (offline) - will resume when online",
        });
      } else {
        updateState("pendingUploads", key, null);
        notify();
        deletePendingUpload(key).catch((deleteErr) => {
          logError(
            "photoTransferQueue:deletePendingUpload:errorCallback",
            deleteErr,
          );
        });

        logQueueError("photoTransferQueue:startUpload:errorCallback", err, {
          key,
          parentId,
          fileName: file?.name,
          fileSize: file?.size,
          fileType: file?.type,
          errorMessage: err.message,
          errorStack: err.stack,
        });

        upsertPhoto(key, {
          status: PhotoStatus.ERROR,
          error: err.message,
          isPaused: false,
          statusMessage: getFriendlyStatusMessage(err),
        });
      }
    },
  });

  uploadTasks.set(key, task);

  getTaskPromise(task)
    .then(async () => {
      if (deletedKeys.has(key)) {
        throw new Error("ZOMBIE_DETECTED");
      }

      updateState("mutationQueue", key, {
        input: mutationInput,
        attempts: 0,
      });
      notify();

      await savePendingMutation(key, mutationInput, parentId, 0);

      if (deletedKeys.has(key)) {
        await deletePendingMutation(key);
        updateState("mutationQueue", key, null);
        notify();
        throw new Error("ZOMBIE_DETECTED");
      }

      updateState("pendingUploads", key, null);
      notify();

      await deletePendingUpload(key).catch((err) =>
        logError("photoTransferQueue:deletePendingUpload:handleUpload", err),
      );
    })
    .then(() => {
      if (deletedKeys.has(key)) {
        return null;
      }

      clearProgressTracking(key);
      upsertPhoto(key, {
        status: PhotoStatus.UPLOADING,
        subStatus: PhotoSubStatus.UPLOADED,
        statusMessage: "Saving to database...",
      });

      if (navigator.onLine) {
        return retryWithBackoff(() =>
          API.graphql(graphqlOperation(createPhoto, { input: mutationInput })),
        );
      }
      return null;
    })
    .then((resp) => {
      if (deletedKeys.has(key)) {
        return;
      }

      clearProgressTracking(key);
      if (resp) {
        updateState("mutationQueue", key, null);
        notify();
        saveQueueToStorage();

        deletePendingMutation(key).catch((err) => {
          logError(
            "photoTransferQueue:deletePendingMutation:handleUpload",
            err,
          );
        });

        const newPhoto = resp.data.createPhoto;
        if (deletedKeys.has(key)) {
          return;
        }
        upsertPhoto(key, {
          ...newPhoto,
          status: PhotoStatus.DONE,
          progress: 1,
          subStatus: null,
          statusMessage: null,
          isPaused: false,
        });
      } else {
        upsertPhoto(key, {
          status: PhotoStatus.UPLOADING,
          subStatus: PhotoSubStatus.PAUSED,
          statusMessage: "Paused (offline) - will retry when online",
          isPaused: true,
        });
      }
    })
    .catch((err) => {
      if (err.message === "ZOMBIE_DETECTED") {
        return;
      }

      clearProgressTracking(key);
      const isNetworkErr =
        err.message?.includes("Network") ||
        err.message?.includes("Failed to fetch") ||
        !navigator.onLine;

      if (!isNetworkErr) {
        logQueueError("photoTransferQueue:startUpload:promiseCatch", err, {
          key,
          parentId,
          fileName: file?.name,
          fileSize: file?.size,
          fileType: file?.type,
          errorMessage: err.message,
          errorStack: err.stack,
        });
      }

      if (isNetworkErr) {
        if (!deletedKeys.has(key)) {
          upsertPhoto(key, {
            status: PhotoStatus.UPLOADING,
            subStatus: PhotoSubStatus.PAUSED,
            statusMessage: "Paused (offline) - will retry when online",
            isPaused: true,
          });

          if (navigator.onLine) {
            setTimeout(() => processMutationQueue(), 1000);
          }
        }
      } else if (!deletedKeys.has(key)) {
        upsertPhoto(key, {
          status: PhotoStatus.ERROR,
          error: err.message,
          statusMessage: getFriendlyStatusMessage(err),
        });
      }
    });
  return true;
};

const requestDelete = async ({ key, photoId, parentId, status }) => {
  if (!key || !parentId) {
    throw new Error("Missing delete information");
  }

  deletedKeys.add(key);

  await addTombstone(key, parentId).catch((err) => {
    logError("photoTransferQueue:addTombstone", err);
  });

  const task = uploadTasks.get(key);
  if (task?.cancel) {
    task.cancel();
  }

  uploadTasks.delete(key);
  updateState("mutationQueue", key, null);
  updateState("pendingUploads", key, null);
  notify();

  deletePendingUpload(key).catch((err) =>
    logError("photoTransferQueue:deletePendingUpload:handleDelete", err),
  );
  deletePendingMutation(key).catch((err) =>
    logError("photoTransferQueue:deletePendingMutation:handleDelete", err),
  );

  const existingPhoto = queueState.photos.get(key);
  removePhoto(key);

  const wasUploaded =
    existingPhoto?.status === PhotoStatus.DONE ||
    existingPhoto?.subStatus === PhotoSubStatus.UPLOADED ||
    (existingPhoto?.progress && existingPhoto.progress >= 1);

  const shouldDeleteRemote =
    status === PhotoStatus.DONE ||
    (status === PhotoStatus.ERROR && wasUploaded);

  if (shouldDeleteRemote) {
    try {
      await Storage.remove(key);

      if (photoId) {
        try {
          await API.graphql(
            graphqlOperation(deletePhoto, {
              input: { id: photoId },
            }),
          );
        } catch (err) {
          logQueueError("photoTransferQueue:requestDelete:deleteById", err, {
            key,
            photoId,
            parentId,
          });
          await deletePhotoByKey(key, parentId);
        }
      } else {
        await deletePhotoByKey(key, parentId);
      }

      setTimeout(() => {
        removeTombstone(key).catch((err) =>
          logError("photoTransferQueue:removeTombstone", err),
        );
      }, 5000);
    } catch (err) {
      if (
        !navigator.onLine ||
        err.message?.includes("Network") ||
        err.message?.includes("Failed to fetch")
      ) {
        updateState("pendingDeletes", key, {
          key,
          photoId,
          parentId,
          attempts: 0,
        });
        notify();

        await savePendingDelete(key, photoId, parentId, 0).catch((e) =>
          logError("photoTransferQueue:savePendingDelete", e),
        );
      } else {
        logError("photoTransferQueue:requestDelete:server", err);
      }
    }
  } else {
    setTimeout(() => {
      removeTombstone(key).catch((err) =>
        logError("photoTransferQueue:removeTombstone:early", err),
      );
    }, 2000);
  }
};

export const photoTransferQueue = {
  init,
  destroy,
  subscribe,
  getSnapshot,
  ensureParentLoaded,
  getParentPhotos,
  startUpload,
  requestDelete,
  processMutationQueue,
  processDeleteQueue,
  retainParent,
  releaseParent,
  setParentMetadata,
};
